import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  CtaBand,
  DirectAnswer,
  Eyebrow,
  Lead,
  PrimaryLink,
  TextLink,
} from "@/components/site/primitives";
import { SITE } from "@/lib/site-content";
import { ogImageMeta, ogImageUrl } from "@/lib/og";
import { articleSchema, jsonLdScript } from "@/lib/schema";
import { contactFunnel } from "@/lib/analytics";
import { RelatedLinks } from "@/components/site/RelatedLinks";

const PATH = "/interim-cto-vs-fractional-cto";
const CTA_SOURCE_HERO = "interim_vs_fractional_hero";
const CTA_SOURCE_BAND = "interim_vs_fractional_footer";
const REVIEWED = "5 August 2026";

const title = "Interim CTO vs Fractional CTO: Which Do You Need?";
const description =
  "Interim fills a vacant seat for a fixed period. Fractional is ongoing part-time. Compare days, mandate, cost and exit before you choose.";

const ANSWER =
  "An interim CTO fills a vacant full-time seat for a fixed period, usually four days a week or more, and leaves when a permanent hire starts. A fractional CTO is an ongoing part-time arrangement sized to the business, often one or two days a month, rising when a programme needs it.";

type Row = { criterion: string; interim: string; fractional: string };

const ROWS: Row[] = [
  {
    criterion: "Why you are hiring",
    interim: "A seat is empty and work is stalling",
    fractional: "The decisions are executive-level; the headcount is not yet",
  },
  {
    criterion: "Time commitment",
    interim: "Most of the working week",
    fractional: "A cadence you set, from monthly to weekly",
  },
  {
    criterion: "Duration",
    interim: "Fixed: typically three to twelve months",
    fractional: "Open-ended, reviewed as the business changes",
  },
  {
    criterion: "Mandate",
    interim: "Full executive authority while in post",
    fractional: "Advise, decide with you, hold suppliers and teams to account",
  },
  {
    criterion: "Cost shape",
    interim: "Close to a full-time executive cost, without equity",
    fractional: "Scales with the time you buy; stops when you stop",
  },
  {
    criterion: "Team management",
    interim: "Line-manages the technical team directly",
    fractional: "Coaches technical leads rather than replacing them",
  },
  {
    criterion: "Exit",
    interim: "Handover to the permanent CTO",
    fractional: "Step down gradually as internal capability grows",
  },
];

const SIGNALS = [
  {
    title: "Signs you need an interim CTO",
    body: "Your CTO has left or is leaving, there is a technical team with nobody running it day to day, and decisions are queuing up waiting for an owner. That is a full-time gap, and a two-days-a-month arrangement will not close it.",
  },
  {
    title: "Signs you need a fractional CTO",
    body: "There is no vacant seat. You have a development agency or a small in-house team, and what is missing is someone senior enough to read what they promised against what they shipped, and to set a plan the board can verify.",
  },
  {
    title: "Signs you need neither yet",
    body: "If the technical decisions still sit comfortably with a founder who understands them, and no external party is being paid on trust, you may be paying for reassurance rather than judgement. I will say so.",
  },
  {
    title: "The two often run in sequence",
    body: "An interim closes an urgent gap, then hands over to a permanent CTO. A fractional arrangement frequently follows, giving the new CTO someone independent to test decisions against for the first year.",
  },
];

const FAQ = [
  {
    q: "Is an interim CTO the same as a fractional CTO?",
    a: "No. An interim CTO is a temporary full-time appointment covering a vacant role for a fixed period. A fractional CTO is a permanent-but-part-time arrangement sized to the stage of the business. The difference is time commitment and mandate, not seniority.",
  },
  {
    q: "Which is cheaper?",
    a: "A fractional arrangement costs less in total because you buy less time, not because the judgement is discounted. If the business genuinely needs a full-time technology leader, fractional is not a cheaper version of that. It is a different arrangement that will not close the gap.",
  },
  {
    q: "How quickly can either start?",
    a: "Both start in days to weeks, which is the main reason companies use them. A permanent CTO appointment is usually a multi-month executive search, and the business rarely has that long when delivery is already slipping.",
  },
  {
    q: "Can a fractional CTO become an interim CTO?",
    a: "It happens, and it should be a deliberate decision rather than scope creep. If the cadence keeps rising because the business now needs daily leadership, the honest answer is that the shape of the arrangement has changed and should be renegotiated.",
  },
  {
    q: "Do either of these replace a non-executive director?",
    a: "No. Both work inside the business on delivery and technical decisions. A non-executive director sits on the board outside the executive, holding it to account, and carries statutory directors' duties under the Companies Act 2006.",
  },
];

export const Route = createFileRoute("/interim-cto-vs-fractional-cto")({
  headers: () => ({
    "cache-control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta("interim-cto-vs-fractional-cto", "Guide: interim CTO vs fractional CTO"),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }],
    scripts: [
      jsonLdScript(
        articleSchema({
          path: PATH,
          headline: title,
          description,
          datePublished: "2026-08-05",
          dateModified: "2026-08-05",
          image: ogImageUrl("interim-cto-vs-fractional-cto"),
        }),
      ),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
            {
              "@type": "ListItem",
              position: 2,
              name: "Fractional CTO",
              item: `${SITE.url}/what-i-do`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Interim CTO vs fractional CTO",
              item: `${SITE.url}${PATH}`,
            },
          ],
        }),
      },
    ],
  }),
  component: InterimVsFractionalPage,
});

function InterimVsFractionalPage() {
  return (
    <div>
      <section
        aria-labelledby="ivf-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Eyebrow>Guide</Eyebrow>
          <h1
            id="ivf-hero-heading"
            className="mt-4 max-w-[22ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            Interim CTO or fractional CTO?
          </h1>
          <DirectAnswer className="mt-6">{ANSWER}</DirectAnswer>
          <Lead className="mt-6">
            Both are senior technology leaders brought in without a permanent hire. They solve
            different problems, and choosing the wrong one is expensive in both directions.
          </Lead>
          <div className="mt-8">
            <PrimaryLink to="/contact" onClick={() => contactFunnel.ctaClick(CTA_SOURCE_HERO)}>
              Arrange a conversation
            </PrimaryLink>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="ivf-table-heading">
        <Container>
          <h2
            id="ivf-table-heading"
            className="max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Interim CTO vs fractional CTO, side by side
          </h2>
          <div className="mt-8 overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[640px] border-collapse text-left text-[15px]">
              <caption className="sr-only">
                Comparison of an interim CTO and a fractional CTO across seven criteria
              </caption>
              <thead className="bg-surface">
                <tr>
                  <th scope="col" className="border-b border-border px-5 py-4 font-semibold">
                    Criterion
                  </th>
                  <th scope="col" className="border-b border-border px-5 py-4 font-semibold">
                    Interim CTO
                  </th>
                  <th scope="col" className="border-b border-border px-5 py-4 font-semibold">
                    Fractional CTO
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.criterion} className="align-top">
                    <th
                      scope="row"
                      className="border-b border-border px-5 py-4 font-semibold text-ink"
                    >
                      {row.criterion}
                    </th>
                    <td className="border-b border-border px-5 py-4 text-body">{row.interim}</td>
                    <td className="border-b border-border px-5 py-4 text-body">{row.fractional}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-5 max-w-[70ch] text-[15px] leading-relaxed text-muted-foreground">
            Neither title has a legal definition, so terms vary between firms. This table describes
            how the two arrangements work in practice, from twenty years of running technology
            organisations as a CTO, a COO and a consultancy founder. It is experience, not survey
            data.
          </p>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="ivf-signals-heading">
        <Container>
          <Eyebrow>How to choose</Eyebrow>
          <h2
            id="ivf-signals-heading"
            className="mt-3 max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Which one your situation calls for
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2">
            {SIGNALS.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="ivf-faq-heading">
        <Container>
          <Eyebrow>Questions</Eyebrow>
          <h2
            id="ivf-faq-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Common questions
          </h2>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {FAQ.map((item) => (
              <div key={item.q} className="px-6 py-6">
                <dt className="text-lg font-semibold text-ink">{item.q}</dt>
                <dd className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-body">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
          <RelatedLinks
            className="mt-7"
            paths={[
              "/fractional-cto-vs-full-time-vs-agency",
              "/when-to-hire-your-first-cto",
              "/fractional-cto-uk",
            ]}
          />
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="ivf-sources-heading">
        <Container>
          <h2 id="ivf-sources-heading" className="text-xl font-bold tracking-[-0.02em]">
            Sources and review
          </h2>
          <p className="mt-4 max-w-[70ch] text-[15px] leading-relaxed text-body">
            This page makes no statistical claims and quotes no market rates, because no credible
            published dataset exists for either arrangement. The statutory point about
            directors&rsquo; duties comes from the Companies Act 2006, sections 170 to 177, and is
            explained further on the{" "}
            <TextLink to="/what-a-technology-non-executive-director-does">
              technology NED guide
            </TextLink>
            .
          </p>
          <p className="mt-4 text-[15px] text-muted-foreground">
            Last reviewed: <time dateTime="2026-08-05">{REVIEWED}</time>
          </p>
        </Container>
      </section>

      <CtaBand
        heading="Not sure which shape fits?"
        body="Describe what has stalled and who is currently accountable for it. I will tell you which arrangement fits, including when the answer is neither."
        to="/contact"
        cta="Arrange a conversation"
        onCtaClick={() => contactFunnel.ctaClick(CTA_SOURCE_BAND)}
      />
    </div>
  );
}
